import { call, all, put, takeLatest } from 'redux-saga/effects'
import {
    getPlace,
    getPlaceByCoordinates,
    getPlaceCopro,
    getPlaceSatelliteImage,
    getPlaceStreetViewImages,
    getHistoricMonuments,
    getBusinessAddressMatch,
    getFireStations,
    getGasStations,
    getWaterWays,
    getRailRoads,
} from './data4homeAPI'
import {
    fetchAddressAction,
    fetchAddressByCoordinatesAction,
    fetchTreesAction,
    fetchSatelliteImageAction,
    fetchStreetViewImagesAction,
    fetchHistoricMonumentsAction,
    setAutoCompleteValue,
    fetchFireStationsAction,
    fetchGasStationsAction,
    fetchWaterWaysAction,
    fetchRailRoadsAction,
} from './addressSlice'
import turfDistance from '@turf/distance'
import { BUILDING_MAP_INTERACTION } from '../../constants/buildingMetric'
import i18n from '../../translation'

function imgURLFromBuffer(buffer: any, format: string) {
    if (!buffer) return undefined;
    const arrayBuffer = new Uint8Array(buffer)
    // const base64Flag = 'data:' + format + ';base64,';
    // const base64Data = buffer.toString('base64');
    // return base64Flag + base64Data;
    const blob = new Blob([arrayBuffer], { type: format });
    const urlCreator = window.URL || window.webkitURL;
    const imageUrl = urlCreator.createObjectURL(blob);
    return imageUrl
}

function getDistanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const distance = turfDistance([lon1, lat1], [lon2, lat2], { units: 'meters' });
    return distance;
}

/** `properties.matchCount` vient de la même recherche que la liste des monuments autour. */
function historicMonumentAddressMatches(payload: any): boolean | undefined {
    if (typeof payload?.properties?.matchCount !== "number") return undefined;
    return payload.properties.matchCount > 0;
}

// TODO: think about how to harmonize this with the rest of the code
/**
 * Wrapper pour getPlaceCopro qui gère les erreurs gracieusement
 * (route de test, ne doit pas bloquer le workflow principal)
 */
async function getPlaceCoproSafe(placeId: string) {
    try {
        return await getPlaceCopro(placeId);
    } catch (error: any) {
        console.warn('Failed to fetch place-copro data:', error);
        return null;
    }
}

// TODO: think about how to harmonize this with the rest of the code
async function getBusinessAddressMatchSafe(placeId: string) {
    try {
        return await getBusinessAddressMatch(placeId);
    } catch (error: any) {
        console.warn('Failed to fetch business-address:', error);
        return null;
    }
}

async function getFireStationsSafe(placeId: string) {
    try {
        return await getFireStations(placeId);
    } catch (error: any) {
        console.warn('Failed to fetch fire station:', error);
        return { type: "FeatureCollection", features: [] };
    }
}

async function getGasStationsSafe(placeId: string) {
    try {
        return await getGasStations(placeId);
    } catch (error: any) {
        console.warn('Failed to fetch gas station:', error);
        return null;
    }
}

function* fetchStreetViewImages(action: any): any {
    try {
        yield put(fetchStreetViewImagesAction.pending())
        const streetViewImageResponse: any = yield call(getPlaceStreetViewImages, action.payload.place_id)
        yield put(fetchStreetViewImagesAction.fulfilled(
            {
                ...streetViewImageResponse,
                streetViewImages: streetViewImageResponse.streetViews.map(function (streetView: any) {
                    return imgURLFromBuffer(streetView.content.data, streetView.contentType)
                }),
            }
        ))
    } catch (error: any) {
        console.log("error", error)
        yield put(fetchStreetViewImagesAction.rejected(error.message))
    }
}

function* fetchSatelliteImage(action: any): any {
    try {
        yield put(fetchSatelliteImageAction.pending())
        const satelliteImageResponse: any = yield call(getPlaceSatelliteImage, action.payload.place_id)
        const { satelliteImage, zoomLevel, fetchedDate } = satelliteImageResponse
        yield put(fetchSatelliteImageAction.fulfilled(imgURLFromBuffer(satelliteImage.content.data, satelliteImage.contentType), zoomLevel, fetchedDate))
    } catch (error: any) {
        console.log("error", error)
        yield put(fetchSatelliteImageAction.rejected(error.message))
    }
}

// function* fetchHistoricMonuments(action: any): any {
//     try {
//         yield put(fetcHistoricMonumentsAction.pending())
//         const historicMonuments: any = yield call(getHistoricMonuments, action.payload.place_id)
//         yield put(fetcHistoricMonumentsAction.fulfilled(historicMonuments))
//         return historicMonuments
//     } catch (error: any) {
//         console.log("error", error)
//         yield put(fetcHistoricMonumentsAction.rejected(error.message))
//     }
// }

// function* fetchFireStations(action: any): any {
//     try {
//         yield put(fetchFireStationsAction.pending())
//         const fireStations: any = yield call(getFireStations, action.payload.place_id)
//         yield put(fetchFireStationsAction.fulfilled(fireStations))
//         return fireStations
//     } catch (error: any) {
//         console.log("error", error)
//         yield put(fetchFireStationsAction.rejected(error.message))
//     }
// }

function* processAddressResult(address: any): any {
    const placeId = address.placeId
    yield put(fetchFireStationsAction.pending())
    yield put(fetchGasStationsAction.pending())
    const [
        historicMonuments,
        waterWays,
        railRoads,
        placeCopro,
        businessAddressMatch,
        fireStations,
        gasStations,
    ] = yield all([
        call(getHistoricMonuments, placeId),
        call(getWaterWays, placeId, 5000),
        call(getRailRoads, placeId, 5000),
        call(getPlaceCoproSafe, placeId),
        call(getBusinessAddressMatchSafe, placeId),
        call(getFireStationsSafe, placeId),
        call(getGasStationsSafe, placeId),
    ])
        const { buildings, inZone, nearZone, altitude, cityCenter, manifestationRoute, trees, ...other } = address

        // TODO: as we dont manipulate the data find out how to get rid of that.
        // I tried to pass address to the put action, but it generated an error. keep it for now
        const result = {
            ...other,
            buildings,
            inZone,
            nearZone,
            cityCenter,
            // volumes,
            // need to convert arraybuffer to url because state allow only serializable values
            // satellite: imgURLFromBuffer(satellite.data.data, satellite.contentType),
            // streetViews: street.map(function (streetView: any) {
            //     return imgURLFromBuffer(streetView.data.data, streetView.contentType)
            // }),
        }

        const buildingData1 = []

        const buildingData2 = []

        buildingData1.push({
            shortLabel: "Surface au sol",
            label: "Surface au sol calculée depuis la geoshape",
            value: buildings.features.length
                ? buildings.features[0] && buildings.features[0].properties.geoshape_computed_area
                : undefined,
            unit: 'sqrt_meter'
        })

        buildingData1.push({
            shortLabel: "Surface habitable",
            label: "Estimation surface habitable",
            value: buildings.features.length
                ? buildings.features[0] && buildings.features[0].properties.living_area_estimation
                : undefined,
            unit: 'sqrt_meter'
        })

        buildingData1.push({
            shortLabel: "Hauteur",
            label: "Hauteur du bâtiment",
            value: buildings.features.length
                ? buildings.features[0] && buildings.features[0].properties.height
                : undefined,
            unit: 'meter'
        })

        if (buildings.features.length) {
            if (buildings.features[0].floor_count) {
                buildingData1.push({
                    shortLabel: "Nombre d'étages",
                    label: "Nombre d'étages",
                    value: buildings.features[0] && buildings.features[0].floor_count,
                    unit: 'number'
                })
            } else {
                buildingData1.push({
                    shortLabel: "Nombre d'étages",
                    label: "Estimation du nombre d'étages",
                    value: buildings.features.length
                        ? buildings.features[0] && buildings.features[0].properties.estimated_floor_count
                        : undefined,
                    unit: 'number'
                })
            }
        }

        buildingData1.push({
            shortLabel: "Année de construction",
            label: "Année de construction",
            value: buildings.features.length
                ? buildings.features[0] && buildings.features[0].properties.construction_year
                : undefined,
            unit: 'year'
        })

        buildingData1.push({
            shortLabel: "Matériau des murs",
            label: "Matériau principal des murs",
            value: buildings.features.length
                ? buildings.features[0] && buildings.features[0].properties.wall_material
                : ["non disponible"],
            unit: 'text'
        })

        buildingData1.push({
            shortLabel: "Matériau de toiture",
            label: "Matériau principal de la toiture",
            value: buildings.features.length
                ? buildings.features[0] && buildings.features[0].properties.roof_material
                : ["non disponible"],
            unit: 'text'
        })

        const zoneBuildingRows: { key: 'qpv' | 'qpvAncienDegrade' | 'qva' | 'zus' | 'zfu' | 'npnru'; shortLabel: string; label: string }[] = [
            { key: 'qpv', shortLabel: 'QPV', label: 'Batiment dans un quartier prioritaire de la ville' },
            { key: 'qpvAncienDegrade', shortLabel: 'QPV ancien ou dégradé', label: 'Batiment dans un quartier prioritaire ancien ou dégradé' },
            { key: 'qva', shortLabel: 'QVA', label: 'Batiment dans un quartier de veille active' },
            { key: 'zus', shortLabel: 'ZUS', label: 'Batiment dans une zone urbaine sensible' },
            { key: 'zfu', shortLabel: 'ZFU', label: 'Batiment dans une zone franche urbaine' },
            { key: 'npnru', shortLabel: 'NPNRU', label: 'Batiment dans un quartier du NPNRU' },
        ]
        zoneBuildingRows.forEach(function ({ key, shortLabel, label }) {
            const hasZoneData = !!inZone
            const inPoly = hasZoneData && (inZone[key]?.features?.length ?? 0) > 0
            buildingData1.push({
                shortLabel,
                label,
                value: hasZoneData ? inPoly : undefined,
                unit: 'bool',
                mapInteraction: BUILDING_MAP_INTERACTION.zonesSurveillees,
                data: nearZone,
            })
        })

        buildingData1.push({
            shortLabel: "Risque de manifestation",
            label: "Risque de manifestation",
            value: manifestationRoute !== null,
            unit: 'bool'
        })

        buildingData1.push({
            shortLabel: "est Monument historique",
            label: "L'adresse correspond à un monument classé ou inscrit MH",
            value: historicMonumentAddressMatches(historicMonuments),
            unit: 'bool',
        })

        buildingData1.push({
            shortLabel: "Monuments historiques autour",
            label: "Bâtiment historique à moins de 500m",
            value: historicMonuments.length
                ? historicMonuments[0].properties.dist.calculated
                : false,
            unit: 'meter_or_boolean',
            data: historicMonuments,
            mapInteraction: BUILDING_MAP_INTERACTION.historicMonumentsAround,
        })

        buildingData1.push({
            shortLabel: "Siège social",
            label: "Au moins un siège social est enregistré à cette adresse",
            value:
                businessAddressMatch == null
                    ? undefined
                    : (businessAddressMatch.properties?.matchCount ?? 0) > 0,
            unit: 'bool',
        })

        buildingData2.push({
            shortLabel: "Altitude",
            label: "Altitude",
            value: altitude && Math.floor(altitude),
            unit: 'meter'
        })

        buildingData2.push({
            shortLabel: "Cours d'eau le plus proche",
            label: "Cours d'eau le plus proche (dans un rayon de 5km)",
            value: waterWays && waterWays.features.length ? waterWays.features[0].properties.name : undefined,
            unit: 'text'
        })

        buildingData2.push({
            shortLabel: "Distance adresse/cours d'eau",
            label: "Distance à vol d'oiseau avec le cours d'eau le plus proche (dans un rayon de 5km)",
            value: waterWays && waterWays.features.length ? Math.floor(waterWays.features[0].properties.distance.calculated) : undefined,
            unit: 'meter'
        })

        const closestWaterWayPoint = !waterWays || !waterWays.features?.length
            ? null
            : waterWays.features.map(function(feature: any) {
                const coordinates: any = [...feature.geometry.coordinates];
                coordinates.feature = feature
                return coordinates
            }).flat().reduce(function(acc: any, point: any) {
                const [lng, lat] = point;
                const distanceMetersOfPoint = getDistanceInMeters(address.latitude, address.longitude, lat, lng);
                if (acc.point === null || distanceMetersOfPoint < acc.distance) {
                  return {point, distance: distanceMetersOfPoint};
                }
                return acc;
            }, {name: null, point: null, distance: null});

        const waterWayHeight = closestWaterWayPoint?.point != null
            ? Math.floor(closestWaterWayPoint.point[2])
            : undefined;

        buildingData2.push({
            shortLabel: "Altitude normale du cours d'eau",
            label: "Altitude normale (hors crue) du cours d'eau le plus proche",
            value: waterWays && waterWays.features.length
                ? waterWayHeight
                : undefined,
            unit: 'meter'
        })
        
        buildingData2.push({
            shortLabel: "Différence hauteur adresse/cours d'eau",
            label: "Différence entre la hauteur à l'adresse et le niveau du cours d'eau le plus proche",
            value: waterWays && waterWays.features.length && altitude && waterWayHeight != null
                ? (Math.floor(altitude) - waterWayHeight)
                : undefined,
            unit: 'meter'
        })

        buildingData2.push({
            shortLabel: "Utilisation du terrain",
            label: "Utilisation du terrain",
            value: (cityCenter && cityCenter.properties.code)
                ? cityCenter.properties.code
                : 'noInfo',
            unit: 'type_urban'
        })

        // Calculate closest rail road point
        const closestRailRoadPoint = !railRoads || !railRoads.features.length 
            ? null 
            : railRoads.features.map(function(feature: any) {
                const coordinates: any = [...feature.geometry.coordinates];
                coordinates.feature = feature
                
                // Normalize MultiLineString to LineString by flattening
                if (feature.geometry.type === 'MultiLineString') {
                    return coordinates.flat();
                }
                // LineString stays as is
                return coordinates;
            }).flat().reduce(function(acc: any, point: any) {
                const [lng, lat] = point;
                const distanceMetersOfPoint = getDistanceInMeters(address.latitude, address.longitude, lat, lng);
                if (acc.point === null || distanceMetersOfPoint < acc.distance) {
                return {point, distance: distanceMetersOfPoint};
                }
                return acc;
            }, {name: null, point: null, distance: null});

        buildingData2.push({
            shortLabel: "Distance voie ferrée",
            label: "Distance à vol d'oiseau avec la voie ferrée la plus proche (dans un rayon de 5km)",
            value: railRoads && railRoads.features.length
                ? Math.floor(closestRailRoadPoint.distance)
                : undefined,
            unit: 'meter'
        })

        const treesRadiusMeters = trees?.properties?.radiusMeters
        buildingData2.push({
            shortLabel: "Arbres à proximité",
            label: treesRadiusMeters != null
                ? `Présence d'arbres dans un rayon de ${treesRadiusMeters} mètres`
                : "Présence d'arbres à proximité",
            value: trees == null
                ? undefined
                : (trees.properties?.count ?? trees.features?.length ?? 0) > 0,
            unit: 'bool',
        })

        const nearestFireStation = fireStations?.features?.[0]
        buildingData2.push({
            shortLabel: i18n.t("fireStation.tileShortLabel"),
            label: i18n.t("fireStation.tileLabel"),
            value: nearestFireStation?.properties?.distance?.calculated != null
                ? Math.floor(nearestFireStation.properties.distance.calculated)
                : undefined,
            unit: 'meter',
            data: fireStations,
            mapInteraction: nearestFireStation
                ? BUILDING_MAP_INTERACTION.fireStation
                : undefined,
        })

        const nearestGasStation = gasStations?.features?.[0]
        const gasStationRadiusMeters = gasStations?.properties?.radiusMeters
        buildingData2.push({
            shortLabel: i18n.t("gasStation.tileShortLabel"),
            label: gasStationRadiusMeters != null
                ? i18n.t("gasStation.tileLabel", { radius: gasStationRadiusMeters })
                : i18n.t("gasStation.tileLabelFallback"),
            value: gasStations == null
                ? undefined
                : (gasStations.properties?.count ?? gasStations.features?.length ?? 0) > 0,
            unit: 'bool',
            data: gasStations,
            mapInteraction: nearestGasStation
                ? BUILDING_MAP_INTERACTION.gasStation
                : undefined,
        })

    yield put(
        fetchAddressAction.fulfilled({
            ...result,
            buildingData1,
            buildingData2,
            placeCopro,
            businessAddressAtPlace: businessAddressMatch,
        })
    )
    yield put(fetchTreesAction.fulfilled(trees))
    yield put(fetchFireStationsAction.fulfilled(fireStations))
    yield put(fetchGasStationsAction.fulfilled(gasStations))
    yield put(fetchHistoricMonumentsAction.fulfilled({...historicMonuments}))
    yield put(fetchWaterWaysAction.fulfilled({...waterWays, closest: closestWaterWayPoint}))
    yield put(fetchRailRoadsAction.fulfilled({...railRoads, closest: closestRailRoadPoint}))
}

function* fetchAddress(action: any): any {
    try {
        yield put(fetchAddressAction.pending())
        const address = yield call(getPlace, action.payload.place_id)
        yield* processAddressResult(address)
    } catch (error: any) {
        console.log("error", error)
        yield put(fetchTreesAction.rejected(error.message))
        yield put(fetchFireStationsAction.rejected(error.message))
        yield put(fetchGasStationsAction.rejected(error.message))
        yield put(fetchAddressAction.rejected(error.message))
    }
}

function* fetchAddressByCoordinates(action: any): any {
    try {
        yield put(fetchAddressAction.pending())
        const address = yield call(getPlaceByCoordinates, action.payload.lat, action.payload.lng)
        yield put(setAutoCompleteValue(address.placeId))
        yield* processAddressResult(address)
    } catch (error: any) {
        console.log("error", error)
        yield put(fetchTreesAction.rejected(error.message))
        yield put(fetchFireStationsAction.rejected(error.message))
        yield put(fetchGasStationsAction.rejected(error.message))
        yield put(fetchAddressAction.rejected(error.message))
    }
}

function* fetchWaterWays(action: any): any {
    try {
        yield put(fetchWaterWaysAction.pending())
        const waterWays: any = yield call(getWaterWays, action.payload.place_id, action.payload.distance)
        yield put(fetchWaterWaysAction.fulfilled(waterWays))
        return waterWays
    } catch (error: any) {
        console.log("error", error)
        yield put(fetchWaterWaysAction.rejected(error.message))
    }
}

function* fetchRailRoads(action: any): any {
    try {
        yield put(fetchRailRoadsAction.pending())
        const railRoads: any = yield call(getRailRoads, action.payload.place_id, action.payload.distance)
        yield put(fetchRailRoadsAction.fulfilled(railRoads))
        return railRoads
    } catch (error: any) {
        console.log("error", error)
        yield put(fetchRailRoadsAction.rejected(error.message))
    }
}

function* adressSaga() {
    yield takeLatest(fetchAddressAction, fetchAddress);
    yield takeLatest(fetchAddressByCoordinatesAction, fetchAddressByCoordinates);
    yield takeLatest(fetchSatelliteImageAction, fetchSatelliteImage);
    yield takeLatest(fetchStreetViewImagesAction, fetchStreetViewImages);
    // yield takeLatest(fetcHistoricMonumentsAction, fetchHistoricMonuments);
    // yield takeLatest(fetchFireStationsAction, fetchFireStations);
    yield takeLatest(fetchWaterWaysAction, fetchWaterWays);
    yield takeLatest(fetchRailRoadsAction, fetchRailRoads);
}

export default adressSaga;